// dev-portal/src/api/client.js

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

if (!API_BASE_URL) {
  console.error('REACT_APP_API_BASE_URL is not defined in .env file');
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    if (activeCompanyId) {
      config.headers['X-Company-Id'] = activeCompanyId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout for 401s on protected routes, not on optional auth checks
    const isProtectedRoute = error.config.url.includes('/dashboard') ||
      error.config.url.includes('/admin') ||
      error.config.url.includes('/api/apps') ||
      error.config.url.includes('/api/keys') ||
      error.config.url.includes('/api/webhooks');

    // Don't logout for marketplace-related calls or when checking current user
    const isMarketplaceContext = window.location.pathname.includes('/marketplace') ||
      window.location.pathname === '/';

    if (error.response?.status === 401 && isProtectedRoute && !isMarketplaceContext) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// API endpoints organized by feature
export const endpoints = {
  // Auth endpoints
  auth: {
    login: '/api/login',
    adminLogin: '/api/admin-login',
    register: '/api/register',
    forgotPassword: '/api/forgot-password',
    resetPassword: '/api/reset-password',
  },

  // Admin sync endpoints
  adminSync: {
    // Trigger sync operations
    trigger: '/api/admin/sync/trigger',
    status: '/api/admin/sync/status',
    health: '/api/admin/sync/health',
    history: '/api/admin/sync/history',

    // Polling control
    startPolling: '/api/admin/sync/polling/start',
    stopPolling: '/api/admin/sync/polling/stop',

    // Queue management
    queueStatus: '/api/admin/sync/queue/status',
    retryFailed: '/api/admin/sync/queue/retry-failed',

    stripe: {
      createCheckoutSession: '/api/stripe/create-checkout-session',
      createGuestCheckoutSession: '/api/stripe/create-guest-checkout-session',
      getPurchaseDetails: (sessionId) => `/api/stripe/purchase/${sessionId}`,
      getMyPurchases: '/api/stripe/my-purchases',
      webhook: '/api/stripe/webhook',
    },
  },

  admin: {
    users: {
      detail: (id) => `/api/admin/users/${id}`,
      keys: (id) => `/api/admin/users/${id}/keys`,
      logs: (id) => `/api/admin/users/${id}/logs`,
      rotateKey: (keyId) => `/api/admin/keys/${keyId}/rotate`,
    }
  },


  // Internal system sync endpoints
  internalSync: {
    // Main sync control
    load: '/api/internal-sync/load',
    status: '/api/internal-sync/status',

    // Features
    features: '/api/internal-sync/features',
    featureDetail: (id) => `/api/internal-sync/features/${id}`,
    linkEvents: (id) => `/api/internal-sync/features/${id}/link-events`,

    // Event types
    eventTypes: '/api/internal-sync/event-types',

    // Tables
    tables: '/api/internal-sync/tables',

    // Relationships
    relationships: '/api/internal-sync/relationships',

    // Metadata
    categories: '/api/internal-sync/categories',
    logs: '/api/internal-sync/logs',
  },

}; // <-- close the endpoints object here

// Internal Sync endpoints
export const internalSync = {
  // Status and overview
  getStatus: () => api.get(endpoints.internalSync.status),

  // Sync operations
  triggerSync: () => api.post(endpoints.internalSync.load),

  // Data endpoints
  getFeatures: (params) => api.get(endpoints.internalSync.features, { params }),
  getFeatureDetail: (id) => api.get(endpoints.internalSync.featureDetail(id)),
  linkFeatureEvents: (id, data) => api.post(endpoints.internalSync.linkEvents(id), data),

  getEventTypes: (params) => api.get(endpoints.internalSync.eventTypes, { params }),
  getTables: (params) => api.get(endpoints.internalSync.tables, { params }),
  getRelationships: (params) => api.get(endpoints.internalSync.relationships, { params }),
  getCategories: () => api.get(endpoints.internalSync.categories),
  getLogs: (params) => api.get(endpoints.internalSync.logs, { params }),
};

// Admin sync endpoints (these are correct)
export const adminSync = {
  getStatus: () => api.get(endpoints.adminSync.status),
  checkHealth: () => api.get(endpoints.adminSync.health),
  triggerSync: (syncType) => api.post(endpoints.adminSync.trigger, { sync_type: syncType }),
};

// Sync helper functions
export const syncHelpers = {
  // Trigger a sync operation - FIX: sync_type should be in the body
  triggerSync: async (syncType) => {
    // Convert enum-like string to match backend expectations
    const syncTypeMap = {
      'legacy_users': 'legacy_users',
      'legacy_events': 'legacy_events',
      'internal_features': 'internal_features',
      'internal_events': 'internal_events',
      'internal_tables': 'internal_tables',
      'internal_relationships': 'internal_relationships',
      'full_sync': 'full_sync'
    };

    return await api.post(endpoints.adminSync.trigger, {
      sync_type: syncTypeMap[syncType] || syncType
    });
  },

  // Get sync status
  getSyncStatus: async () => {
    return await api.get(endpoints.adminSync.status);
  },

  // Start event polling
  startEventPolling: async (interval = 60) => {
    return await api.post(`${endpoints.adminSync.startPolling}?interval=${interval}`);
  },

  // Stop event polling
  stopEventPolling: async () => {
    return await api.post(endpoints.adminSync.stopPolling);
  },

  // Load internal system data
  loadInternalData: async () => {
    return await api.post(endpoints.internalSync.load);
  },

  // Get internal sync status
  getInternalSyncStatus: async () => {
    return await api.get(endpoints.internalSync.status);
  },

  // Fetch features with filters
  fetchFeatures: async (params = {}) => {
    return await api.get(endpoints.internalSync.features, { params });
  },

  // Link events to a feature
  linkFeatureEvents: async (featureId, eventIds, tableIds = []) => {
    return await api.post(endpoints.internalSync.linkEvents(featureId), {
      feature_id: featureId,
      event_type_ids: eventIds,
      table_ids: tableIds,
    });
  },
  // Marketplace endpoints
  marketplace: {
    // Get all marketplace apps
    getApps: '/api/marketplace/apps',

    // Get app by ID (legacy - for backward compatibility)
    getAppById: (id) => `/api/marketplace/apps/${id}`,

    // Get app by slug (new SEO-friendly)
    getAppBySlug: (slug) => `/api/marketplace/apps/by-slug/${slug}`,

    // Get apps by category
    getAppsByCategory: (categorySlug) => `/api/marketplace/apps/category/${categorySlug}`,

    // Install app
    installApp: (appId) => `/api/marketplace/apps/${appId}/install`,

    // Get user's installed apps
    //getMyApps: '/api/marketplace/my-apps',
  },
};

// Marketplace helper functions
export const marketplace = {
  // Get all marketplace apps
  getApps: () => api.get(endpoints.marketplace.getApps),

  // Get app details by slug (preferred)
  getAppBySlug: (slug) => api.get(endpoints.marketplace.getAppBySlug(slug)),

  // Get app details by ID (legacy support)
  getAppById: (id) => api.get(endpoints.marketplace.getAppById(id)),

  // Smart function that handles both ID and slug
  getApp: (identifier) => {
    // Check if it's a numeric ID or a slug
    if (!isNaN(identifier)) {
      return api.get(endpoints.marketplace.getAppById(identifier));
    }
    return api.get(endpoints.marketplace.getAppBySlug(identifier));
  },

  // Get apps by category
  getAppsByCategory: (categorySlug, params = {}) =>
    api.get(endpoints.marketplace.getAppsByCategory(categorySlug), { params }),

  // Install an app
  installApp: (appId) => api.post(endpoints.marketplace.installApp(appId)),

  // Get user's installed apps
  getMyApps: () => api.get(endpoints.marketplace.getMyApps),
};

// Export for use in components
export { marketplace as marketplaceAPI };

